import React, { useState, useEffect } from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  InformationCircleIcon,
  ArrowTopRightOnSquareIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { format, differenceInDays } from 'date-fns';
import { supabase } from '../../../lib/supabase-auth';
import type { Task } from '../TaskSidePanel';

interface ComplianceRequirement {
  id: string;
  title: string;
  description: string;
  category: string;
  status: 'complete' | 'incomplete' | 'pending' | 'not_applicable';
  dueDate?: string;
  expiryDate?: string;
  lastCompletedDate?: string;
  documents?: string[];
  regulatory_reference?: string;
  help_text?: string;
  wizard_link?: string;
}

interface ComplianceTabProps {
  task: Task;
}

export default function ComplianceTab({ task }: ComplianceTabProps) {
  const [requirements, setRequirements] = useState<ComplianceRequirement[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  useEffect(() => {
    fetchComplianceRequirements();
  }, [task.id]);

  const fetchComplianceRequirements = async () => {
    try {
      const { data, error } = await supabase
        .from('compliance_requirements')
        .select('*')
        .eq('task_id', task.id)
        .order('due_date', { ascending: true, nullsFirst: false });

      if (error) throw error;
      setRequirements(data || []);
    } catch (error) {
      console.error('Error fetching compliance requirements:', error);
      // Use mock data for demonstration
      setRequirements(mockRequirements);
    } finally {
      setLoading(false);
    }
  };

  // Mock data for demonstration
  const mockRequirements: ComplianceRequirement[] = [
    {
      id: '1',
      title: 'Know Your Client (KYC) Documentation',
      description: 'Complete client identification and verification process',
      category: 'AML/KYC',
      status: 'complete',
      lastCompletedDate: '2024-01-10T00:00:00Z',
      expiryDate: '2025-01-10T00:00:00Z',
      documents: ['client-id.pdf', 'address-proof.pdf'],
      regulatory_reference: 'FINTRAC Guidelines Section 64',
      help_text: 'KYC documentation must be updated annually or when client information changes significantly.',
      wizard_link: '/compliance/kyc-wizard',
    },
    {
      id: '2',
      title: 'Investment Policy Statement (IPS)',
      description: 'Document investment objectives, risk tolerance, and constraints',
      category: 'Client Suitability',
      status: 'pending',
      dueDate: '2024-02-01T00:00:00Z',
      regulatory_reference: 'IIROC Rule 3200',
      help_text: 'IPS must be reviewed and updated at least annually or when client circumstances change.',
      wizard_link: '/compliance/ips-wizard',
    },
    {
      id: '3',
      title: 'Anti-Money Laundering Risk Assessment',
      description: 'Assess client risk level for money laundering activities',
      category: 'AML/KYC',
      status: 'incomplete',
      dueDate: '2024-01-20T00:00:00Z',
      regulatory_reference: 'PCMLTFA Requirements',
      help_text: 'Risk assessment must consider client type, geography, products, and transaction patterns.',
    },
    {
      id: '4',
      title: 'Suitability Assessment',
      description: 'Ensure investment recommendations are suitable for client',
      category: 'Client Suitability',
      status: 'complete',
      lastCompletedDate: '2024-01-15T00:00:00Z',
      regulatory_reference: 'IIROC Rule 1300',
      help_text: 'Suitability must be assessed before each transaction and during account reviews.',
    },
    {
      id: '5',
      title: 'Privacy Consent',
      description: 'Obtain client consent for collection and use of personal information',
      category: 'Privacy & Consent',
      status: 'complete',
      lastCompletedDate: '2023-12-01T00:00:00Z',
      expiryDate: '2024-12-01T00:00:00Z',
      documents: ['privacy-consent.pdf'],
      regulatory_reference: 'PIPEDA',
      help_text: 'Privacy consent should be renewed annually or when privacy policies change.',
    },
  ];

  const categories = ['all', ...new Set(requirements.map(req => req.category))];
  const filteredRequirements = selectedCategory === 'all' 
    ? requirements 
    : requirements.filter(req => req.category === selectedCategory);

  const getStatusIcon = (requirement: ComplianceRequirement) => {
    // Check for expiry first
    if (requirement.expiryDate) {
      const daysUntilExpiry = differenceInDays(new Date(requirement.expiryDate), new Date());
      if (daysUntilExpiry < 0) {
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      } else if (daysUntilExpiry < 30) {
        return <ExclamationTriangleIcon className="h-5 w-5 text-amber-500" />;
      }
    }

    switch (requirement.status) {
      case 'complete':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'incomplete':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'pending':
        return <ClockIcon className="h-5 w-5 text-amber-500" />;
      case 'not_applicable':
        return <InformationCircleIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusText = (requirement: ComplianceRequirement) => {
    if (requirement.expiryDate) {
      const daysUntilExpiry = differenceInDays(new Date(requirement.expiryDate), new Date());
      if (daysUntilExpiry < 0) {
        return { text: 'Expired', className: 'text-red-600 font-medium' };
      } else if (daysUntilExpiry < 30) {
        return { text: `Expires in ${daysUntilExpiry} days`, className: 'text-amber-600 font-medium' };
      }
    }

    if (requirement.dueDate && requirement.status !== 'complete') {
      const daysUntilDue = differenceInDays(new Date(requirement.dueDate), new Date());
      if (daysUntilDue < 0) {
        return { text: 'Overdue', className: 'text-red-600 font-medium' };
      } else if (daysUntilDue < 7) {
        return { text: `Due in ${daysUntilDue} days`, className: 'text-amber-600' };
      }
    }

    switch (requirement.status) {
      case 'complete':
        return { text: 'Complete', className: 'text-green-600' };
      case 'incomplete':
        return { text: 'Incomplete', className: 'text-red-600' };
      case 'pending':
        return { text: 'Pending Review', className: 'text-amber-600' };
      case 'not_applicable':
        return { text: 'Not Applicable', className: 'text-gray-500' };
    }
  };

  const handleLaunchWizard = (wizardLink: string) => {
    // In a real app, this would navigate to the wizard
    window.open(wizardLink, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Category Filter */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Filter by Category
        </label>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="block w-full px-3 py-2 text-gray-900 border border-gray-300 rounded-md focus:ring-amber-500 focus:border-amber-500"
        >
          {categories.map((category) => (
            <option key={category} value={category}>
              {category === 'all' ? 'All Categories' : category}
            </option>
          ))}
        </select>
      </div>

      {/* Requirements List */}
      <div className="space-y-4">
        {filteredRequirements.length === 0 ? (
          <p className="text-center text-gray-500 py-8">
            No compliance requirements found
          </p>
        ) : (
          filteredRequirements.map((requirement) => {
            const statusInfo = getStatusText(requirement);
            
            return (
              <div
                key={requirement.id}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1">
                    {getStatusIcon(requirement)}
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-gray-900">
                        {requirement.title}
                      </h4>
                      <p className="text-sm text-gray-500 mt-1">
                        {requirement.description}
                      </p>
                      
                      <div className="mt-2 flex items-center space-x-4 text-xs">
                        <span className="text-gray-500">
                          Category: <span className="font-medium">{requirement.category}</span>
                        </span>
                        <span className={statusInfo.className}>
                          {statusInfo.text}
                        </span>
                      </div>

                      {requirement.regulatory_reference && (
                        <div className="mt-2 text-xs text-gray-500">
                          Reference: {requirement.regulatory_reference}
                        </div>
                      )}

                      {requirement.lastCompletedDate && (
                        <div className="mt-1 text-xs text-gray-500">
                          Last completed: {format(new Date(requirement.lastCompletedDate), 'MMM d, yyyy')}
                        </div>
                      )}

                      {requirement.help_text && (
                        <div className="mt-3 p-3 bg-blue-50 rounded-md">
                          <div className="flex">
                            <InformationCircleIcon className="h-5 w-5 text-blue-400 flex-shrink-0" />
                            <p className="ml-2 text-xs text-blue-700">
                              {requirement.help_text}
                            </p>
                          </div>
                        </div>
                      )}

                      {requirement.documents && requirement.documents.length > 0 && (
                        <div className="mt-3">
                          <p className="text-xs font-medium text-gray-700 mb-1">
                            Related Documents:
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {requirement.documents.map((doc, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-gray-100 text-gray-700"
                              >
                                <DocumentTextIcon className="h-3 w-3 mr-1" />
                                {doc}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {requirement.wizard_link && requirement.status !== 'complete' && (
                    <button
                      onClick={() => handleLaunchWizard(requirement.wizard_link)}
                      className="ml-4 inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
                    >
                      Launch Wizard
                      <ArrowTopRightOnSquareIcon className="ml-1 h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Compliance Summary */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h4 className="text-sm font-medium text-gray-900 mb-2">
          Compliance Summary
        </h4>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Complete:</span>
            <span className="ml-2 font-medium text-green-600">
              {requirements.filter(r => r.status === 'complete').length}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Pending:</span>
            <span className="ml-2 font-medium text-amber-600">
              {requirements.filter(r => r.status === 'pending').length}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Incomplete:</span>
            <span className="ml-2 font-medium text-red-600">
              {requirements.filter(r => r.status === 'incomplete').length}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Expiring Soon:</span>
            <span className="ml-2 font-medium text-amber-600">
              {requirements.filter(r => {
                if (!r.expiryDate) return false;
                const days = differenceInDays(new Date(r.expiryDate), new Date());
                return days >= 0 && days < 30;
              }).length}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}